import { ChevronDown, Search, ShieldAlert } from 'lucide-react';
import { Fragment, type ReactNode, useId, useState } from 'react';
import { type Permission, type PermissionEffect, PERMISSIONS } from '#shared/types';
import { Checkbox } from '#client/components/ui/Checkbox';
import { cn } from '#client/lib/cn';
import {
  MATRIX_KEYS,
  type MatrixModule,
  type OverrideState,
  type Overrides,
  filterMatrix,
  grantState,
  nextOverride,
  STATE_LABEL,
  grantTooltip,
  overrideTooltip,
  setGranted,
  setOverride,
} from './role-matrix.logic';

/**
 * Ticking permissions on a role: one checkbox per action, with "all" per row
 * and per module. Ticking an action brings along what it needs ("Edit" brings
 * "View"); unticking takes away what needs it.
 */
interface GrantModeProps {
  mode: 'grant';
  selected: ReadonlySet<Permission>;
  onChange?: ((next: Set<Permission>) => void) | undefined;
}

/**
 * Exceptions for one person: each action is inherited from their role, or
 * explicitly allowed, or explicitly denied. A click cycles through the three.
 */
interface OverrideModeProps {
  mode: 'override';
  rolePermissions: ReadonlySet<Permission>;
  overrides: Overrides;
  effective: ReadonlySet<Permission>;
  onChange?: ((next: Map<Permission, PermissionEffect>) => void) | undefined;
}

type PermissionMatrixProps = (GrantModeProps | OverrideModeProps) & {
  /** Without `onChange` the matrix is read-only regardless. */
  readOnly?: boolean | undefined;
};

export function PermissionMatrix(props: PermissionMatrixProps) {
  const readOnly = props.readOnly === true || props.onChange === undefined;

  if (props.mode === 'grant') {
    const { selected, onChange } = props;
    return (
      <MatrixLayout
        granted={selected}
        renderCell={(permission) => (
          <GrantCell
            permission={permission}
            selected={selected}
            onChange={readOnly ? undefined : onChange}
          />
        )}
        renderGroupToggle={
          readOnly
            ? undefined
            : ({ keys, label, isLabelVisible }) => (
                <AllToggle
                  label={label}
                  isLabelVisible={isLabelVisible}
                  selected={selected}
                  keys={keys}
                  onChange={onChange}
                />
              )
        }
      />
    );
  }

  return (
    <MatrixLayout
      granted={props.effective}
      legend={<OverrideLegend />}
      renderCell={(permission) => (
        <OverrideCell permission={permission} matrix={props} readOnly={readOnly} />
      )}
    />
  );
}

/** A "select everything here" control for a module or a multi-action row. */
type GroupToggle = (group: {
  keys: readonly Permission[];
  label: string;
  isLabelVisible: boolean;
}) => ReactNode;

interface MatrixLayoutProps {
  /** What the counters count — the role's grants, or a person's effective set. */
  granted: ReadonlySet<Permission>;
  legend?: ReactNode;
  renderCell: (permission: Permission) => ReactNode;
  /** Omitted when nothing can be ticked in bulk. */
  renderGroupToggle?: GroupToggle | undefined;
}

/** The searchable, collapsible grid both modes share; each supplies its own cells. */
function MatrixLayout({ granted, legend, renderCell, renderGroupToggle }: MatrixLayoutProps) {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const searchId = useId();
  const modules = filterMatrix(query);

  const handleToggleModule = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="c-perm-matrix">
      <div className="c-perm-matrix__toolbar">
        <label className="c-perm-matrix__search" htmlFor={searchId}>
          <Search aria-hidden="true" />
          <span className="sr-only">Search permissions</span>
          <input
            id={searchId}
            type="search"
            placeholder="Search permissions — “delete”, “stock”, “money”…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <span className="c-perm-matrix__count" aria-live="polite">
          {granted.size} of {MATRIX_KEYS.length} permissions
        </span>
        <button
          type="button"
          className="c-perm-matrix__link"
          onClick={() =>
            setCollapsed(
              collapsed.size > 0
                ? new Set()
                : new Set(modules.map((permissionModule) => permissionModule.key)),
            )
          }
        >
          {collapsed.size > 0 ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      {legend}

      {modules.length === 0 && (
        <p className="c-perm-matrix__empty">No permission matches “{query}”.</p>
      )}

      {modules.map((permissionModule) => (
        <ModuleSection
          key={permissionModule.key}
          permissionModule={permissionModule}
          isExpanded={query.trim() !== '' || !collapsed.has(permissionModule.key)}
          onToggle={() => handleToggleModule(permissionModule.key)}
          granted={granted}
          renderCell={renderCell}
          renderGroupToggle={renderGroupToggle}
        />
      ))}
    </div>
  );
}

function OverrideLegend() {
  return (
    <p className="c-perm-matrix__legend">
      <span className="c-perm-state c-perm-state--inherit is-on">From role</span>
      <span className="c-perm-state c-perm-state--allow">Allowed</span>
      <span className="c-perm-state c-perm-state--deny">Denied</span>
      <span>Click an action to cycle through them.</span>
    </p>
  );
}

function ModuleSection({
  permissionModule,
  isExpanded,
  onToggle,
  granted,
  renderCell,
  renderGroupToggle,
}: Omit<MatrixLayoutProps, 'legend'> & {
  permissionModule: MatrixModule;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const keys = permissionModule.rows.flatMap((row) => row.keys);
  const held = keys.filter((key) => granted.has(key)).length;
  const bodyId = useId();

  return (
    <section className="c-perm-module">
      <header className="c-perm-module__head">
        <button
          type="button"
          className="c-perm-module__toggle"
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <ChevronDown aria-hidden="true" className={cn(!isExpanded && 'is-collapsed')} />
          <span className="c-perm-module__title">{permissionModule.label}</span>
          <span className="c-perm-module__desc">{permissionModule.description}</span>
        </button>
        <span
          className={cn(
            'c-perm-module__tally',
            held === keys.length && 'is-full',
            held === 0 && 'is-empty',
          )}
        >
          {held}/{keys.length}
        </span>
        {renderGroupToggle?.({
          keys,
          label: `All ${permissionModule.label} permissions`,
          isLabelVisible: false,
        })}
      </header>

      {isExpanded && (
        <div id={bodyId} className="c-perm-module__rows">
          {permissionModule.rows.map((row) => (
            <div className="c-perm-row" key={row.label}>
              <div className="c-perm-row__label">
                {renderGroupToggle && row.keys.length > 1 ? (
                  renderGroupToggle({ keys: row.keys, label: row.label, isLabelVisible: true })
                ) : (
                  <span>{row.label}</span>
                )}
              </div>
              <div className="c-perm-row__actions">
                {row.keys.map((key) => (
                  <Fragment key={key}>{renderCell(key)}</Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** A tri-state "everything here" box for a row or a module. */
function AllToggle({
  label,
  isLabelVisible,
  selected,
  keys,
  onChange,
}: {
  label: string;
  isLabelVisible: boolean;
  selected: ReadonlySet<Permission>;
  keys: readonly Permission[];
  onChange: ((next: Set<Permission>) => void) | undefined;
}) {
  const state = grantState(selected, keys);
  return (
    <Checkbox
      label={isLabelVisible ? <strong>{label}</strong> : <span className="sr-only">{label}</span>}
      checked={state === 'all'}
      indeterminate={state === 'some'}
      onChange={(event) => onChange?.(setGranted(selected, keys, event.target.checked))}
      className={cn(!isLabelVisible && 'c-perm-all')}
    />
  );
}

function GrantCell({
  permission,
  selected,
  onChange,
}: {
  permission: Permission;
  selected: ReadonlySet<Permission>;
  onChange: ((next: Set<Permission>) => void) | undefined;
}) {
  const def = PERMISSIONS[permission];
  const title = grantTooltip(permission);

  return (
    <span className={cn('c-perm-cell', selected.has(permission) && 'is-on')} title={title}>
      <Checkbox
        label={
          <>
            {def.action}
            {def.sensitive && <SensitiveMark />}
          </>
        }
        checked={selected.has(permission)}
        disabled={onChange === undefined}
        onChange={(event) => onChange?.(setGranted(selected, [permission], event.target.checked))}
      />
    </span>
  );
}

function OverrideCell({
  permission,
  matrix,
  readOnly,
}: {
  permission: Permission;
  matrix: OverrideModeProps;
  readOnly: boolean;
}) {
  const def = PERMISSIONS[permission];
  const fromRole = matrix.rolePermissions.has(permission);
  const state: OverrideState = matrix.overrides.get(permission) ?? 'inherit';
  const on = matrix.effective.has(permission);
  const title = overrideTooltip(permission, { state, isGranted: on, isFromRole: fromRole });

  return (
    <button
      type="button"
      className={cn('c-perm-state', `c-perm-state--${state}`, on && 'is-on')}
      title={title}
      aria-label={`${def.action}: ${on ? 'granted' : 'not granted'}, ${STATE_LABEL[state]}`}
      disabled={readOnly}
      onClick={() =>
        matrix.onChange?.(setOverride(matrix.overrides, permission, nextOverride(state, fromRole)))
      }
    >
      <span aria-hidden="true" className="c-perm-state__dot" />
      {def.action}
      {def.sensitive && <SensitiveMark />}
    </button>
  );
}

function SensitiveMark() {
  return (
    <span className="c-perm-sensitive" title="Sensitive: money, everyone's data, or irreversible">
      <ShieldAlert aria-hidden="true" />
      <span className="sr-only">(sensitive)</span>
    </span>
  );
}
